import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import Dashboard from '../components/Dashboard';
import ProfilePage from '../components/profile/ProfilePage';
import AppLayout from '../components/layout/AppLayout';
import { UserRole } from '../types/auth';
import TimeEntrySection from '../components/collaborator/TimeEntrySection';
import MyTasksSection from '../components/collaborator/MyTasksSection';
import PDIList from '../components/pdi/PDIList';
import ReimbursementList from '../components/reimbursement/ReimbursementList';
import VacationRequestList from '../components/vacation/VacationRequestList';
import AprovacoesList from '../components/aprovacoes/AprovacoesList';
import ColaboradoresList from '../components/colaboradores/ColaboradoresList';
import FeriasAprovacoesList from '../components/ferias/FeriasAprovacoesList';
import RelatoriosList from '../components/relatorios/RelatoriosList';
import ManagerDashboard from '../components/manager/ManagerDashboard';
import ProjectList from '../components/manager/ProjectList';
import ProjectDetails from '../components/manager/ProjectDetails';
import ProjectSimulation from '../components/manager/ProjectSimulation';
import ClientList from '../components/manager/ClientList';
import { VacancyManagement } from '../pages/VacancyManagement';
import { VacancyApplications } from '../pages/VacancyApplications';
import { VacancyEdit } from '../pages/VacancyEdit';
import { TalentPool } from '../pages/TalentPool';

const NotFound = () => (
    <Box p={3}>
        <Typography variant="h4" gutterBottom>
            Página não encontrada
        </Typography>
        <Typography>
            A página que você está procurando não existe.
        </Typography>
    </Box>
);

const ProtectedRoutes: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const isManager = user?.role === UserRole.GESTOR.toString() || user?.role === UserRole.DIRETOR.toString();

    // Sem sessão: vai direto ao login (antes a página abria, chamava a API e caía em "sessão expirou")
    if (!isAuthenticated || !user) {
        return <Navigate to="/login" replace />;
    }

    // Conta criada/redefinida pelo RH ou admin inicial: só o perfil (troca de senha) até trocar
    if (user.must_change_password) {
        return (
            <AppLayout>
                <Routes>
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="*" element={<Navigate to="/profile" replace />} />
                </Routes>
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <Routes>
                {/* Rotas comuns */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/horas" element={<TimeEntrySection />} />
                <Route path="/minhas-tarefas" element={<MyTasksSection />} />
                <Route path="/pdi" element={<PDIList />} />
                <Route path="/reembolsos" element={<ReimbursementList />} />
                <Route path="/ferias" element={<VacationRequestList />} />

                {/* Rotas de RH */}
                {(user?.role === UserRole.RH.toString() || user?.role === UserRole.DIRETOR.toString()) && (
                    <>
                        <Route path="/colaboradores" element={<ColaboradoresList />} />
                        <Route path="/aprovacoes" element={<AprovacoesList />} />
                        <Route path="/ferias-aprovacoes" element={<FeriasAprovacoesList />} />
                        <Route path="/relatorios" element={<RelatoriosList />} />
                        <Route path="/vagas" element={<VacancyManagement />} />
                        <Route path="/vagas/nova" element={<VacancyEdit />} />
                        <Route path="/vagas/:id/edit" element={<VacancyEdit />} />
                        <Route path="/vagas/:id/candidaturas" element={<VacancyApplications />} />
                        <Route path="/talent-pool" element={<TalentPool />} />
                    </>
                )}

                {/* Rotas de Gestor */}
                {isManager && (
                    <>
                        <Route path="/gestor/dashboard" element={<ManagerDashboard />} />
                        <Route path="/gestor/projetos" element={<ProjectList />} />
                        <Route path="/gestor/projetos/:id" element={<ProjectDetails />} />
                        <Route path="/gestor/projetos/:id/simulacao/:simulationId" element={<ProjectSimulation />} />
                        <Route path="/gestor/projetos/:id/simulacao/nova" element={<ProjectSimulation />} />
                        <Route path="/gestor/clientes" element={<ClientList />} />
                    </>
                )}

                <Route path="/" element={
                    isManager ? <Navigate to="/gestor/dashboard" replace /> : <Navigate to="/dashboard" replace />
                } />
                
                {/* Página 404 */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </AppLayout>
    );
};

export default ProtectedRoutes; 